import { useEffect, useState, useRef } from 'react';
import { calculateTopicScore } from '../../util/surveyUtils';

const Question = ({
  index,
  firstQuestion,
  answer,
  startText,
  endText,
  onChange,
  type,
  allAnswers = [], // <-- expects allAnswers prop from SurveyQuestion
  topicsData = [], // <-- expects topicsData prop from SurveyQuestion
  desiredScores = {},
  setDesiredScores = () => {},
  isExtendedSurvey = false, // <-- new prop to determine if extended survey
  isLastQuestionInSurvey = false, // <-- new prop to determine if last question in survey
  onNext = () => {}, // <-- new prop to handle navigation to next question
  ...question
}) => {
  const [isTaken, setIsTaken] = useState(question?.isTaken || false);
  const [satisfactionChoice, setSatisfactionChoice] = useState('');
// desiredScores and setDesiredScores are now passed as props from SurveyQuestion
  // console.log("DESIRED SCORES", desiredScores);
  const [previousTopicId, setPreviousTopicId] = useState(null);
  const [lastCompletedTopicId, setLastCompletedTopicId] = useState(null);
  const [showNewTopicText, setShowNewTopicText] = useState(false);
  const [currentQuestionId, setCurrentQuestionId] = useState(null);
  const [answered, setAnswered] = useState(false);
  
  // Refs for auto-focusing on answer elements
  const rangeSliderRef = useRef(null);
  const firstRadioRef = useRef(null);
  const firstChoiceRef = useRef(null);
  
  // Refs for modal accessibility
  const modalRef = useRef(null);
  const modalFirstRadioRef = useRef(null);
  const modalSliderRef = useRef(null);
  const modalButtonRef = useRef(null);

  useEffect(() => {
    const topicId = question?.topic?._id;
    const questionId = question?._id;
    console.log("EFFECT", {topicId, previousTopicId, index, firstQuestion});

    // Nieuwe vraag → reset "answered"
    if (questionId !== currentQuestionId) {
      setAnswered(false);
      setCurrentQuestionId(questionId);
    }

    if (
      topicId &&
      topicId !== previousTopicId &&
      typeof index === 'number' &&
      index > 0 &&
      previousTopicId !== null
    ) {
      // We gaan naar een nieuw topic: sla het vorige topic op als 'afgerond'
      setLastCompletedTopicId(previousTopicId);
      setShowNewTopicText(true);
      setPreviousTopicId(topicId);
      console.log('[Modal Debug] Switched topic, set lastCompletedTopicId:', previousTopicId);
    } else if (topicId !== previousTopicId) {
      // Alleen bij de allereerste vraag: update previousTopicId zonder modal
      setPreviousTopicId(topicId);
    }
  }, [question, index, firstQuestion]);

  useEffect(() => {
    if (answered) {
      setShowNewTopicText(false);
    }
  }, [answered]);

  const updateTooltip = () => {
    const range = document.querySelector('#range-slider');
    const tooltip = document.querySelector('#range-tooltip');

    if (!range || !tooltip) return;

    let thumbnWidth = 14;
    const minValue = 1;
    const maxValue = 10;
    let offset = range.clientWidth / (maxValue - minValue);
    let rangeValue = range.valueAsNumber === 0 ? 1 : range.valueAsNumber;

    let left =
      (rangeValue - minValue) * offset -
      ((rangeValue - minValue) / (maxValue - minValue) - 0.5) * thumbnWidth;

    tooltip.style.setProperty('--left', `${left - 14}px`);
    tooltip.textContent = rangeValue;
  };

  const handleChange = (e) => {
    let value = e.target.value;
    
    // For multiple choice questions, handle both old string format and new object format
    if (type === 'multiple') {
      // Try to parse as JSON (new format with score)
      try {
        const choiceData = JSON.parse(value);
        // Pass the choice data object which includes both text and score
        onChange(choiceData);
      } catch {
        // Fallback for old string format (backward compatibility)
        onChange(value);
      }
    } else {
      onChange(value);
    }
    
    if (type === 'range') {
      updateTooltip();
    }
    setShowNewTopicText(false);
    setIsTaken(true);
    setAnswered(true);
  };

  const handleCheckboxChange = (e) => {
    e.target.value = e.target.checked ? 0 : 1;
    handleChange(e);
    setShowNewTopicText(false);
    setAnswered(true);
  };

  useEffect(() => {
    if (type === 'range') {
      updateTooltip();
    }
  }, [index, type, answer]);

  useEffect(() => {
    const isTopicChanged = index > 0 && question.topic._id !== allAnswers[index - 1].topic._id;

    if (isTopicChanged) {
      setShowNewTopicText(true);
      setLastCompletedTopicId(allAnswers[index - 1].topic._id);
    } else if (isLastQuestionInSurvey) {
      setShowNewTopicText(true);
      setLastCompletedTopicId(question.topic._id);
    }
  }, [index, question.topic._id, allAnswers, isLastQuestionInSurvey, setShowNewTopicText, setLastCompletedTopicId]);

  // Auto-focus on answer elements when question loads or changes
  useEffect(() => {
    // Only focus if question is visible (not showing new topic modal)
    if (!showNewTopicText && !answered) {
      const focusElement = () => {
        if (type === 'range' && rangeSliderRef.current) {
          rangeSliderRef.current.focus();
        } else if (type === 'yes/no' && firstRadioRef.current) {
          firstRadioRef.current.focus();
        } else if (type === 'multiple' && firstChoiceRef.current) {
          firstChoiceRef.current.focus();
        }
      };
      
      // Small delay to ensure DOM is ready
      const timeoutId = setTimeout(focusElement, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [type, showNewTopicText, answered, currentQuestionId]);

  // Handle Enter key to go to next question
  useEffect(() => {
    const handleKeyDown = (event) => {
      // Only handle Enter key if question is answered and not showing modal
      if (event.key === 'Enter' && isTaken && !showNewTopicText) {
        event.preventDefault();
        onNext();
      }
    };

    // Add event listener to document
    document.addEventListener('keydown', handleKeyDown);

    // Cleanup event listener on unmount
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isTaken, showNewTopicText, onNext]);

  // Handle number keys for range sliders
  useEffect(() => {
    const handleNumberKeys = (event) => {
      // Only handle if we're not in an input field and not showing modal
      if (event.target.tagName !== 'INPUT' || event.target.type === 'range') {
        const key = event.key;
        const numValue = parseInt(key);
        
        // Check if it's a valid number key (1-9, we'll treat 0 as 10)
        if (key >= '1' && key <= '9') {
          event.preventDefault();
          
          // For main question range slider
          if (type === 'range' && !showNewTopicText) {
            const targetValue = numValue;
            if (targetValue >= 1 && targetValue <= 10) {
              // Create a synthetic event to trigger handleChange
              const syntheticEvent = {
                target: { value: targetValue.toString() }
              };
              handleChange(syntheticEvent);
              
              // Update the range slider value visually
              if (rangeSliderRef.current) {
                rangeSliderRef.current.value = targetValue;
                updateTooltip();
              }
            }
          }
          
          // For modal range slider (desired score)
          if (showNewTopicText && satisfactionChoice === 'not-satisfied') {
            const targetValue = numValue;
            if (targetValue >= 1 && targetValue <= 10) {
              setDesiredScores(prev => ({ ...prev, [lastCompletedTopicId]: targetValue }));
              
              // Update the modal slider value visually
              if (modalSliderRef.current) {
                modalSliderRef.current.value = targetValue;
              }
            }
          }
        }
        
        // Handle '0' key as value 10
        if (key === '0') {
          event.preventDefault();
          
          // For main question range slider
          if (type === 'range' && !showNewTopicText) {
            const syntheticEvent = {
              target: { value: '10' }
            };
            handleChange(syntheticEvent);
            
            if (rangeSliderRef.current) {
              rangeSliderRef.current.value = '10';
              updateTooltip();
            }
          }
          
          // For modal range slider
          if (showNewTopicText && satisfactionChoice === 'not-satisfied') {
            setDesiredScores(prev => ({ ...prev, [lastCompletedTopicId]: 10 }));
            
            if (modalSliderRef.current) {
              modalSliderRef.current.value = '10';
            }
          }
        }
      }
    };
    
    document.addEventListener('keydown', handleNumberKeys);
    
    return () => {
      document.removeEventListener('keydown', handleNumberKeys);
    };
  }, [type, showNewTopicText, satisfactionChoice, lastCompletedTopicId, handleChange]);

  // Modal keyboard accessibility
  useEffect(() => {
    if (showNewTopicText) {
      // Focus management when modal opens
      const focusFirstElement = () => {
        if (modalFirstRadioRef.current) {
          modalFirstRadioRef.current.focus();
        }
      };
      
      // Small delay to ensure modal is rendered
      const timeoutId = setTimeout(focusFirstElement, 100);
      
      // Note: Escape key is disabled for this modal since an answer is required
      
      // Handle Tab key to trap focus within modal
      const handleTabKey = (event) => {
        if (event.key === 'Tab') {
          const focusableElements = modalRef.current?.querySelectorAll(
            'input[type="radio"]:not([disabled]), input[type="range"]:not([disabled]), button:not([disabled])'
          );
          
          if (focusableElements && focusableElements.length > 0) {
            const firstElement = focusableElements[0];
            const lastElement = focusableElements[focusableElements.length - 1];
            
            if (event.shiftKey) {
              // Shift + Tab: go to previous element
              if (document.activeElement === firstElement) {
                event.preventDefault();
                lastElement.focus();
              }
            } else {
              // Tab: go to next element
              if (document.activeElement === lastElement) {
                event.preventDefault();
                firstElement.focus();
              }
            }
          }
        }
      };
      
      // Handle Enter key in modal
      const handleEnterInModal = (event) => {
        if (event.key === 'Enter' && modalRef.current?.contains(document.activeElement)) {
          // Don't trigger if we're on a radio button (let it handle its own selection first)
          if (document.activeElement.type === 'radio') {
            return; // Let radio button handle its own Enter behavior
          }
          
          // Only proceed if a satisfaction choice has been made
          if (satisfactionChoice) {
            event.preventDefault();
            handleStartQuestions();
          }
        }
      };
      
      document.addEventListener('keydown', handleTabKey);
      document.addEventListener('keydown', handleEnterInModal);
      
      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener('keydown', handleTabKey);
        document.removeEventListener('keydown', handleEnterInModal);
      };
    }
  }, [showNewTopicText]);

  // Auto-focus on modal slider when "not-satisfied" is selected
  useEffect(() => {
    if (showNewTopicText && satisfactionChoice === 'not-satisfied') {
      // Small delay to ensure slider is rendered
      const timeoutId = setTimeout(() => {
        if (modalSliderRef.current) {
          modalSliderRef.current.focus();
        }
      }, 100);
      
      return () => clearTimeout(timeoutId);
    }
  }, [satisfactionChoice, showNewTopicText]);

const handleStartQuestions = () => {
  setShowNewTopicText(false);
};

  console.log("RENDER", {showNewTopicText, firstQuestion, index, topicId: question?.topic?._id, previousTopicId});
  return (
    <>
      {showNewTopicText && isExtendedSurvey && (() => {
        // Check if the completed topic is 'Handelingsruimte' and exclude it from modal
        if (lastCompletedTopicId && topicsData && topicsData.length > 0) {
          // Helper function to find topic by ID (reused from below)
          function findTopicById(topics, id) {
            if (!topics || !id) return undefined;
            const idStr = typeof id === 'object' && id !== null ? String(id._id) : String(id);
            for (const topic of topics) {
              if (String(topic._id) === idStr) return topic;
              if (topic.subTopics && Array.isArray(topic.subTopics)) {
                const found = topic.subTopics.find(sub => String(sub._id) === idStr);
                if (found) return found;
              }
            }
            return undefined;
          }
          const completedTopic = findTopicById(topicsData, lastCompletedTopicId);
          if (completedTopic && completedTopic.title === 'Handelingsruimte') {
            return false; // Don't show modal for Handelingsruimte
          }
        }
        return true; // Show modal for all other topics
      })() ? (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-80">
          <div 
            ref={modalRef}
            className="bg-white rounded-2xl shadow-lg max-w-md w-full p-8 text-center z-[10000] relative"
            role="dialog"
            aria-modal="true"
            aria-labelledby="modal-title"
          >
            {/* Show computed score for previous topic if available */}
            {(() => {
              // Gebruik nu lastCompletedTopicId voor de score
              let scoreTopicId = lastCompletedTopicId;
              if (scoreTopicId && allAnswers && allAnswers.length > 0) {
                // Inspect topic answers and numeric answers
                const topicAnswers = allAnswers.filter(
                  (a) => a.topic && (a.topic._id === scoreTopicId || a.topic === scoreTopicId)
                );
                const numericAnswers = topicAnswers
                  .map((a) => (typeof a.answer === 'number' ? a.answer : parseFloat(a.answer)))
                  .filter((ans) => !isNaN(ans) && ans > 0);
                const score = calculateTopicScore(allAnswers, scoreTopicId);
                if (score !== null) {
                  return (
                    <div className="mb-6 font-medium text-sm md:text-lg mb-8 md:mb-20">
                      {(() => {
                        // Zoek de titel van het topic waar de score op is behaald
                        // Helper om topic te zoeken in topicsData en subTopics
                        function findTopicById(topics, id) {
                          if (!topics || !id) return undefined;
                          const idStr = typeof id === 'object' && id !== null ? String(id._id) : String(id);
                          for (const topic of topics) {
                            if (String(topic._id) === idStr) return topic;
                            if (topic.subTopics && Array.isArray(topic.subTopics)) {
                              const found = topic.subTopics.find(sub => String(sub._id) === idStr);
                              if (found) return found;
                            }
                          }
                          return undefined;
                        }
                        const scoredTopic = findTopicById(topicsData, scoreTopicId);
                        console.log("scoredTopic", scoredTopic);

                        if (scoredTopic && scoredTopic.parent) {
                          // subtopic: toon ook hoofdtopic
                          const mainTopic = topicsData?.find(t => String(t._id) === String(scoredTopic.parent));
                          return (
                            <>
                              Je geeft jezelf op het thema <strong>{scoredTopic.title}</strong> een gemiddeld cijfer van: <span><strong>{score}</strong></span>
                            </>
                          );
                        } else {
                          // hoofdtopic of niet gevonden
                          return (
                            <>
                              Je geeft jezelf op het thema <strong>{scoredTopic?.title || ''}</strong> een gemiddeld cijfer van: <span><strong>{score}</strong></span>
                            </>
                          );
                        }
                      })()}
                    </div>
                  );
                } else {
                  console.log('[Modal Debug] No score to display (score is null)');
                }
              } else {
                if (!scoreTopicId) console.log('[Modal Debug] No lastCompletedTopicId');
                if (!allAnswers || allAnswers.length === 0) console.log('[Modal Debug] No allAnswers or empty');
              }
              return null;
            })()}
            <div className="mt-6 font-medium text-sm md:text-lg mb-8 md:mb-20">
              <div className="flex flex-col items-start gap-4">
                <label className="flex items-center gap-2">
                  <input
                    ref={modalFirstRadioRef}
                    type="radio"
                    name="satisfaction-choice"
                    value="satisfied"
                    checked={satisfactionChoice === 'satisfied'}
                    onChange={() => {
                      setSatisfactionChoice('satisfied');
                      // Bij tevreden: verwijder gewenste score voor dit topic
                      if (lastCompletedTopicId) {
                        setDesiredScores(prev => {
                          const copy = { ...prev };
                          delete copy[lastCompletedTopicId];
                          return copy;
                        });
                      }
                    }}
                  />
                  Ik ben tevreden met dit cijfer
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="satisfaction-choice"
                    value="not-satisfied"
                    checked={satisfactionChoice === 'not-satisfied'}
                    onChange={() => setSatisfactionChoice('not-satisfied')}
                  />
                  Ik zou meer tevreden zijn met het cijfer...
                </label>
              </div>
              {/* Alleen slider tonen als tweede optie gekozen is */}
              {satisfactionChoice === 'not-satisfied' && (
                <div className="relative w-full flex flex-col items-center mt-6">
                  <span className="mb-2 text-2xl font-bold text-green-900" id="desired-score-value">{Number(desiredScores?.[lastCompletedTopicId] ?? 5).toFixed(1)}</span>
                  <input
                    ref={modalSliderRef}
                    type="range"
                    id="desired-score-slider"
                    min={1}
                    max={10}
                    step={0.5}
                    value={desiredScores?.[lastCompletedTopicId] ?? 5}
                    onChange={e => {
                      const val = Number(e.target.value);
                      setDesiredScores(prev => ({ ...prev, [lastCompletedTopicId]: val }));
                    }}
                    className="w-full bg-transparent range-slider focus:outline-none"
                    style={{ accentColor: '#f2c83b' }}
                    aria-label="Gewenste score"
                  />
                  <div className="w-full flex justify-between text-sm md:text-lg font-semibold mt-1">
                    <span>1</span>
                    <span>10</span>
                  </div>
                </div>
              )}
            </div>
            <button
              ref={modalButtonRef}
              className="text-white py-2 px-6 rounded-xl hover:bg-blue-700 transition mt-6" 
              style={{backgroundColor: '#f2c83b', color: 'black'}}
              onClick={handleStartQuestions}
              aria-label={`Ga verder met het thema ${question.topic.title}`}
            >
              Ga verder met het thema {question.topic.title}
            </button>
          </div>
        </div>
      ) : (
        <div>
          <h3 className="text-lg sm:text-xl md:text-2xl font-semibold mb-4">
            {question.topic?.parent ? `${topicsData.find(t => t._id === question.topic.parent)?.title} - ${question.topic.title}` : question.topic?.title}
          </h3>
          <h5 className="font-medium text-sm md:text-lg mb-8 md:mb-20">
            {index + 1}. {question?.title}
          </h5>
          <div>
            <div className="flex flex-wrap lg:flex-nowrap relative items-start">
              <div
                className={`min-w-max pr-10 text-sm md:text-lg font-medium flex items-center ${
                  answer === 0 && 'opacity-50'
                }`}
              >
                <span>{question?.property?.startText}</span>
              </div>

              {type === 'range' && (
                <div className="pt-1 w-full">
                  <div className="relative">
                    <span id="range-tooltip">1</span>
                    <input
                      ref={rangeSliderRef}
                      type="range"
                      step={1}
                      disabled={answer === 0}
                      className="bg-transparent range-slider disabled:opacity-50 disabled:pointer-events-none"
                      id="range-slider"
                      onChange={handleChange}
                      min={1}
                      max={10}
                      value={answer}
                    />
                  </div>
                  <div
                    className={`w-full flex justify-between text-sm md:text-lg font-semibold ${
                      answer === 0 && 'opacity-50'
                    }`}
                  >
                    <span className="block font-medium">1</span>
                    <span className="block font-medium">10</span>
                  </div>
                </div>
              )}

              {type === 'yes/no' && (
                <div className={`pt-1 w-full ${answer === 0 && 'opacity-50'}`}>
                  <div id="group1" className="flex flex-row gap-3 justify-center">
                    <label className="flex cursor-pointer gap-2">
                      <input
                        ref={firstRadioRef}
                        type="radio"
                        disabled={answer === 0}
                        className="w-4 h-4 border border-gray-800 rounded-full focus:ring-2 focus:ring-gray-300 cursor-pointer"
                        name="group1"
                        onChange={handleChange}
                        checked={isTaken && answer === 10}
                        value="10"
                      />
                      <span>Ja</span>
                    </label>
                    <label className="flex cursor-pointer gap-2">
                      <input
                        type="radio"
                        disabled={answer === 0}
                        className="w-4 h-4 border border-gray-800 rounded-full focus:ring-2 focus:ring-gray-300 cursor-pointer"
                        name="group1"
                        onChange={handleChange}
                        checked={isTaken && answer === 1}
                        value="1"
                      />
                      <span>Nee</span>
                    </label>
                  </div>
                </div>
              )}

              {type === 'multiple' && question?.property?.choices?.length > 0 && (
                <div className={`pt-1 w-full ${answer === 0 && 'opacity-50'}`}>
                  <ul
                    className="flex flex-wrap gap-6 pl-[2px] max-h-44 overflow-y-auto"
                    id="choice-select"
                  >
                    {question?.property?.choices.map((choice, index) => {
                      // Handle both old string format and new object format
                      const choiceText = typeof choice === 'string' ? choice : choice.text;
                      const choiceScore = typeof choice === 'string' ? null : choice.score;
                      const choiceValue = typeof choice === 'string' ? choice : JSON.stringify(choice);
                      
                      // For checking if this choice is selected, compare appropriately
                      const isSelected = isTaken && (
                        (typeof answer === 'string' && answer === choiceText) ||
                        (typeof answer === 'object' && answer?.text === choiceText)
                      );
                      
                      return (
                        <li className="mb-2 flex items-center gap-2" key={index}>
                          <input
                            id={`choice-${index}`}
                            name="choice-select"
                            type="radio"
                            className="w-4 h-4 border border-gray-800 rounded-full focus:ring-2 focus:ring-gray-300 cursor-pointer"
                            disabled={answer === 0}
                            onChange={handleChange}
                            value={choiceValue}
                            checked={isSelected}
                            ref={index === 0 ? firstChoiceRef : null}
                          />
                          <label
                            htmlFor={`choice-${index}`}
                            className="cursor-pointer capitalize"
                          >
                            {choiceText}
                          </label>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}

              <div
                className={`min-w-max text-sm md:text-lg lg:pl-10 font-medium flex items-center ${
                  answer === 0 && 'opacity-50'
                }`}
              >
                <span>{question?.property?.endText}</span>
              </div>
            </div>

            <div className="flex items-center space-x-2 mt-5">
              <input
                id="custom-checkbox"
                type="checkbox"
                className="w-5 h-5 border border-gray-800 rounded focus:ring-2 focus:ring-gray-300 cursor-pointer"
                onChange={handleCheckboxChange}
                checked={isTaken && answer === 0}
                value="0"
              />
              <label htmlFor="custom-checkbox" className="cursor-pointer">
                N.v.t
              </label>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Question;
